import { AfterViewInit, Component, ElementRef, Input, NgZone, OnInit, ViewChild } from '@angular/core';
import { AlertController, GestureController, ModalController, ToastController } from '@ionic/angular';
import { HttpsService } from 'src/app/service/https.service';
import { ImageCacheService } from 'src/app/service/image-cache.service';

interface GalleryImage {
  url: string;
  url_original: string;
  imagem_id: number;
  cache_key?: string;
  size: number;
  descricao?: string;
}

@Component({
  standalone: false,
  selector: 'app-gallery-modal',
  templateUrl: './gallery-modal.component.html',
  styleUrls: ['./gallery-modal.component.scss'],
})
export class GalleryModalComponent implements OnInit, AfterViewInit {
  @Input() images: GalleryImage[] = [];
  @Input() initialIndex = 0;
  @Input() check_etapa_ambiente: any;
  @Input() etapaNome = '';
  @Input() ambienteNome = '';

  @ViewChild('carousel') carousel!: ElementRef<HTMLElement>;

  currentIndex = 0;
  private removedImageIds: number[] = [];

  constructor(
    private modalController: ModalController,
    private imageCacheService: ImageCacheService,
    private alertCtrl: AlertController,
    private https: HttpsService,
    private toastCtrl: ToastController,
    private gestureCtrl: GestureController,
    private ngZone: NgZone
  ) {}

  ngOnInit() {
    this.currentIndex = Math.min(Math.max(this.initialIndex, 0), Math.max(this.images.length - 1, 0));
  }

  ngAfterViewInit() {
    if (!this.carousel?.nativeElement) {
      return;
    }
    const gesture = this.gestureCtrl.create({
      el: this.carousel.nativeElement,
      gestureName: 'gallery-swipe',
      onMove: event => this.onSwipeMove(event),
      onEnd: event => this.ngZone.run(() => this.onSwipeEnd(event))
    });
    gesture.enable();
  }

  get currentImage(): GalleryImage | undefined {
    return this.images[this.currentIndex];
  }

  onSwipeMove(event: any) {
    const carouselWidth = this.carousel?.nativeElement.clientWidth || window.innerWidth;
    const displacement = (event.deltaX / carouselWidth) * 100;
    this.carousel.nativeElement.style.transform = `translateX(${(-this.currentIndex * 100) + displacement}%)`;
  }

  onSwipeEnd(event: any) {
    if (event.deltaX > 50) {
      this.prev();
    } else if (event.deltaX < -50) {
      this.next();
    }
    this.updateCarouselPosition();
  }

  close() {
    void this.modalController.dismiss({
      removedImageIds: this.removedImageIds,
      etapa_ambiente: this.check_etapa_ambiente
    });
  }

  next() {
    if (this.images.length < 2) return;
    this.currentIndex = this.currentIndex < this.images.length - 1 ? this.currentIndex + 1 : 0;
    this.updateCarouselPosition();
  }

  prev() {
    if (this.images.length < 2) return;
    this.currentIndex = this.currentIndex > 0 ? this.currentIndex - 1 : this.images.length - 1;
    this.updateCarouselPosition();
  }

  selectImage(index: number) {
    this.currentIndex = index;
    this.updateCarouselPosition();
  }

  async remove_imagem(imagemId: number, urlOriginal: string, size: number) {
    const alert = await this.alertCtrl.create({
      header: 'Excluir imagem?',
      message: 'A imagem será removida permanentemente do relatório fotográfico da obra.',
      cssClass: ['custom-alert', 'alert-danger'],
      buttons: [
        { text: 'Cancelar', role: 'cancel' },
        {
          text: 'Excluir',
          handler: async () => {
            const formData = new FormData();
            formData.append('data', JSON.stringify({
              callback: 'delete_imagem',
              imagem_id: imagemId,
              size
            }));

            const result: any = await this.https.post(formData, 'relatorio_fotografico.php');
            if (!result.success) {
              await this.showMessage('Não foi possível excluir', result.msg, result.class || 'danger');
              return;
            }

            const indexToRemove = this.images.findIndex(image => image.imagem_id === imagemId);
            const cacheKey = indexToRemove >= 0
              ? (this.images[indexToRemove].cache_key || urlOriginal)
              : urlOriginal;
            if (indexToRemove >= 0) {
              this.images.splice(indexToRemove, 1);
            }
            this.removedImageIds.push(imagemId);
            this.currentIndex = Math.min(this.currentIndex, Math.max(0, this.images.length - 1));
            await this.imageCacheService.clearCacheForImage(cacheKey);
            await this.showMessage('Imagem removida', 'A imagem foi excluída com sucesso.', 'success');

            if (this.images.length === 0) {
              this.close();
              return;
            }
            this.updateCarouselPosition();
          }
        }
      ]
    });
    await alert.present();
  }

  private updateCarouselPosition() {
    if (this.carousel?.nativeElement) {
      this.carousel.nativeElement.style.transform = `translateX(${-this.currentIndex * 100}%)`;
    }
  }

  private async showMessage(header: string, message: string, cssClass: string) {
    const toast = await this.toastCtrl.create({
      header,
      message,
      duration: 1600,
      position: 'middle',
      cssClass
    });
    await toast.present();
  }
}
